import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import Card from 'tio-reimagined/components/card';
import Chip from 'tio-reimagined/components/chip';
import type { ChipIntent } from 'tio-reimagined/components/chip';
import SectionHeader from 'tio-reimagined/components/section-header';
import Text from 'tio-reimagined/components/text';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import { PhBuildings, PhCheckCircle, PhCircle, PhWarning } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type PslfFormModel from 'tio-data/models/pslf-form';

interface EmploymentPeriod {
  employerName: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  status: EmploymentStatus;
}

type EmploymentStatus = 'verified' | 'pending' | 'in_progress' | 'requires_verification';

const STATUS_PRIORITY: Record<EmploymentStatus, number> = {
  in_progress: 0,
  requires_verification: 1,
  pending: 2,
  verified: 3,
};

interface S {
  Args: {
    forms: PslfFormModel[];
  };
  Element: HTMLDivElement;
}

function chipIntentForStatus(status: EmploymentStatus): ChipIntent {
  switch (status) {
    case 'verified':
      return 'success';
    case 'pending':
      return 'warning';
    case 'requires_verification':
      return 'warning';
    case 'in_progress':
      return 'default';
  }
}

function statusForPriorForm(form: PslfFormModel): EmploymentStatus {
  if (form.isSubmitted) {
    return 'verified';
  }
  return 'in_progress';
}

export default class PslfQualifyingEmployment extends Component<S> {
  @service declare intl: IntlService;

  get employmentPeriods(): EmploymentPeriod[] {
    const currentForms = this.args.forms.filter((f) => f.isCurrentEmployer);
    const priorForms = this.args.forms.filter((f) => !f.isCurrentEmployer);

    const periods: EmploymentPeriod[] = [];

    // Collapse all current employer forms into a single entry
    if (currentForms.length > 0) {
      // Use the most recent current form for display data
      const latestCurrent = currentForms.sort((a, b) =>
        (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
      )[0]!;

      const employerName = latestCurrent.employerDisplayName || 'Unknown Employer';
      const startDate = latestCurrent.employmentStartDate;

      // An expiring form (11+ months old) needs recertification — show warning regardless
      const hasExpiringForm = currentForms.some((f) => f.isExpiring);

      // Any current employer form submitted in the past ~12 months counts as verified
      const hasRecentSubmission = currentForms.some((f) => f.isRecentlySubmitted);

      // Any form not rejected/cancelled/expired/abandoned counts as active (in progress)
      const hasActiveForm = currentForms.some((form) => !form.isInactiveStatus);

      let currentStatus: EmploymentStatus = 'requires_verification';
      if (!hasExpiringForm && hasRecentSubmission) {
        currentStatus = 'verified';
      } else if (!hasExpiringForm && hasActiveForm) {
        currentStatus = 'in_progress';
      }

      periods.push({
        employerName,
        startDate,
        endDate: null,
        isCurrent: true,
        status: currentStatus,
      });
    }

    // Deduplicate prior employer forms by employer name + start date
    const priorMap = new Map<string, EmploymentPeriod>();
    for (const form of priorForms) {
      const employerName = form.employerDisplayName || 'Unknown Employer';
      const startDate = form.employmentStartDate;
      const endDate = form.employmentEndDate;
      const status = statusForPriorForm(form);

      const key = `${employerName.toLowerCase()}|${startDate ?? ''}`;
      const existing = priorMap.get(key);

      if (!existing || STATUS_PRIORITY[status] > STATUS_PRIORITY[existing.status]) {
        priorMap.set(key, { employerName, startDate, endDate, isCurrent: false, status });
      }
    }

    // Sort prior periods by start date descending
    const sortedPrior = [...priorMap.values()].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return b.startDate.localeCompare(a.startDate);
    });

    return [...periods, ...sortedPrior];
  }

  statusLabel = (status: EmploymentStatus): string => {
    switch (status) {
      case 'verified':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.verified');
      case 'pending':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.pending');
      case 'in_progress':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.in_progress');
      case 'requires_verification':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.requires_verification');
    }
  };

  <template>
    <Card @padding="lg" @roundedXl={{true}} ...attributes>
      <SectionHeader
        @icon={{PhBuildings}}
        @title={{t "pslf.reimagined.qualifying_employment.heading"}}
        @iconClass="text-teal-600"
        @level={{2}}
      />
      <div class="space-y-4">
        {{#each this.employmentPeriods as |period|}}
          <div class="flex items-start gap-2">
            {{#if (isVerified period.status)}}
              <PhCheckCircle @size={{20}} @weight="fill" class="shrink-0 mt-0.5 text-teal-600" />
            {{else if (isRequiresVerification period.status)}}
              <PhWarning @size={{20}} @weight="fill" class="shrink-0 mt-0.5 text-amber-500" />
            {{else}}
              <PhCircle @size={{20}} @weight="fill" class="shrink-0 mt-0.5 text-gray-400" />
            {{/if}}
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2 flex-wrap">
                <Text @weight="semibold">
                  {{period.employerName}}
                </Text>
                <Chip @intent={{chipIntentForStatus period.status}} @size="sm">
                  {{this.statusLabel period.status}}
                </Chip>
              </div>
              <Text @variant="muted" @size="sm">
                {{#if period.startDate}}
                  {{safeFormatDate period.startDate month="short" year="numeric"}}
                {{else}}
                  —
                {{/if}}
                -
                {{#if period.isCurrent}}
                  {{t "pslf.reimagined.qualifying_employment.current"}}
                {{else if period.endDate}}
                  {{safeFormatDate period.endDate month="short" year="numeric"}}
                {{else}}
                  —
                {{/if}}
              </Text>
            </div>
          </div>
        {{/each}}

        <Text @variant="muted">
          {{t "pslf.reimagined.qualifying_employment.description"}}
        </Text>
      </div>
    </Card>
  </template>
}

function isVerified(status: EmploymentStatus): boolean {
  return status === 'verified';
}

function isRequiresVerification(status: EmploymentStatus): boolean {
  return status === 'requires_verification';
}
